import React, { useEffect, useState } from "react";
import {
  Button,
  Layout,
  Row,
  Col,
  Typography,
  Tooltip,
  Modal,
  Table,
  Input,
  message,
  Spin,
  Breadcrumb,
  Tag,
  Switch,
  Dropdown,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/th";
import type { TableProps } from "antd";
import { useAuth } from "../../utils/Auth/AuthWrapper";
import { useNavigate } from "react-router-dom";
import "../taskCategory/styles/index.less";
import {
  corpus_getAll,
  corpus_delete,
  corpus_updateStatus,
  corpus_download,
} from "../../services/copus";
import { useTranslation } from "react-i18next";

const { Content } = Layout;
const { Title } = Typography;
const { Search } = Input;
const { confirm } = Modal;

interface DataType {
  key: string;
  corpus_id: string;
  title: string;
  owner_name: string;
  owner_contact: string;
  source_reference: string;
  description: string;
  file_path: string;
  publish_year: number;
  publish_status: number;
  created_date: any;
}

type Props = {};

const Corpuspage = (props: Props) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState<DataType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  // ดึงข้อมูล Corpus
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await getCorpusDetail(searchText);
      setIsLoading(false);
    };

    fetchData();
  }, [searchText]);

  // get data จาก API
  const getCorpusDetail = async (searchValue: string = "") => {
    try {
      // Member จะเห็นเฉพาะ corpus ที่ publish แล้ว (status = 1)
      const isAdmin = ["Super Admin", "Administrator"].includes(auth.user.role);

      const res = await corpus_getAll({
        fields: [
          "*",
        ],
        filter: {
          page: 1,
          size: 100,
          search: searchValue || null,
          status: isAdmin ? null : 1,
          lasted_update: null,
        },
      });

      if (res.result) {
        setFilteredData(res.result);
      }
    } catch (e) {
      console.error("Error fetching corpus:", e);
      message.error("ไม่สามารถโหลดข้อมูล Corpus ได้");
    }
  };

  // search
  const onSearch = (value: any) => {
    setSearchText(value);
  };

  // api delete
  const showDeleteConfirm = (corpus_id: string, title: string) => {
    confirm({
      title: t("delete title"),
      icon: <ExclamationCircleFilled style={{ color: "#FF3B30" }} />,
      content: (
        <div
          className="custom-modal-content-news"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <p>{t("delete detail category")} :</p>
          <p>
            <strong>{title}</strong>
          </p>
          <p>{t("yes or no")}</p>
        </div>
      ),
      okText: t("confirm"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk: async () => {
        try {
          const res = await corpus_delete({ corpus_id: corpus_id });

          if (res.status === "success") {
            message.success("Delete Success!");
            getCorpusDetail(searchText); // รีโหลดข้อมูล
          } else {
            message.error("ไม่สามารถลบ Corpus ได้");
          }
        } catch (e) {
          console.error(e);
          message.error("เกิดข้อผิดพลาดในการลบ Corpus");
        }
      },
      onCancel() {},
    });
  };

  // เปลี่ยนสถานะการเผยแพร่
  const handleToggleStatus = async (
    corpus_id: string,
    currentStatus: number
  ) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await corpus_updateStatus({
        corpus_id,
        publish_status: newStatus,
      });

      if (res.result) {
        message.success(
          newStatus === 1
            ? "เผยแพร่ Corpus แล้ว"
            : "ยกเลิกการเผยแพร่ Corpus แล้ว"
        );
        getCorpusDetail(searchText); // รีโหลดข้อมูล
      }
    } catch (error) {
      console.error("Error updating corpus status:", error);
      message.error("ไม่สามารถเปลี่ยนสถานะได้");
    }
  };

  // table
  const columns: TableProps<DataType>["columns"] = [
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {t("name corpus")}
        </div>
      ),
      dataIndex: "title",
      key: "title",
      width: 150,
      ellipsis: true,
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {t("owner_name")}
        </div>
      ),
      dataIndex: "owner_name",
      key: "owner_name",
      width: 120,
      ellipsis: true,
      responsive: ["md"] as any,
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {t("Description")}
        </div>
      ),
      dataIndex: "description",
      key: "description",
      width: 400,
      render: (text: string) => {
        if (!text) return "-";

        return (
          <Tooltip
            title={<div style={{ maxHeight: 300, overflow: "auto" }} dangerouslySetInnerHTML={{ __html: text }} />}
            overlayStyle={{ maxWidth: 500 }}
            placement="rightTop"
            autoAdjustOverflow={false}
            color="#00215E"
          >
            <div
              style={{
                cursor: "pointer",
                maxHeight: 200,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 8,
                WebkitBoxOrient: "vertical",
              }}
              dangerouslySetInnerHTML={{ __html: text }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {t("source reference")}
        </div>
      ),
      dataIndex: "source_reference",
      key: "source_reference",
      width: 150,
      ellipsis: true,
      responsive: ["lg"] as any,
      render: (text: string) => {
        if (!text) return "-";
        const isUrl = /^(https?:\/\/)/.test(text) || /^www\./i.test(text) || /\.(com|net|org|edu|gov|io|co|th|app|dev)($|\/)/i.test(text);
        if (isUrl) {
          const url = /^https?:\/\//i.test(text) ? text : `https://${text}`;

          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1890ff", textDecoration: "underline" }}
            >
              {text}
            </a>
          );
        }

        return text;
      },
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {t("Download")}
        </div>
      ),
      dataIndex: "file_path",
      key: "download",
      width: "10%",
      align: "center" as const,
      render: (file_path: string, record: DataType) => {
        if (!file_path) return "-";

        try {
          const files = JSON.parse(file_path);
          if (!Array.isArray(files) || files.length === 0) return "-";

          const handleDownload = async (fileId: string) => {
            const selectedFile = files.find((f: any) => f.id === fileId);
            if (!selectedFile) return;

            try {
              const response = await corpus_download({
                corpus_id: record.corpus_id,
                file_id: fileId,
              });

              if (response.blob) {
                const url = window.URL.createObjectURL(response.blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = response.filename || selectedFile.name || "download";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                message.success("ดาวน์โหลดสำเร็จ");
              } else {
                message.error("ไม่สามารถดาวน์โหลดไฟล์ได้");
              }
            } catch (error) {
              console.error("Download error:", error);
              message.error("เกิดข้อผิดพลาดในการดาวน์โหลด");
            }
          };

          const handleDownloadAll = async () => {
            message.loading({ content: "กำลังดาวน์โหลดไฟล์ทั้งหมด...", key: "downloadAll" });
            let successCount = 0;
            let failCount = 0;

            for (const file of files) {
              try {
                const response = await corpus_download({
                  corpus_id: record.corpus_id,
                  file_id: file.id,
                });

                if (response.blob) {
                  const url = window.URL.createObjectURL(response.blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = response.filename || file.name || "download";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                  successCount++;
                } else {
                  failCount++;
                }
              } catch (error) {
                console.error("Download error:", error);
                failCount++;
              }
              // Add small delay between downloads to prevent browser blocking
              await new Promise(resolve => setTimeout(resolve, 500));
            }

            if (failCount === 0) {
              message.success({ content: `ดาวน์โหลดสำเร็จ ${successCount} ไฟล์`, key: "downloadAll" });
            } else {
              message.warning({ content: `ดาวน์โหลดสำเร็จ ${successCount} ไฟล์, ล้มเหลว ${failCount} ไฟล์`, key: "downloadAll" });
            }
          };

          // Calculate total file size
          const totalSize = files.reduce((sum: number, file: any) => sum + (file.filesize_mb || 0), 0);

          const menuItems = [
            // Download All option (show only if more than 1 file)
            ...(files.length > 1 ? [{
              key: "download-all",
              label: (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", fontWeight: 600, color: "#1890ff" }}>
                  <span><DownloadOutlined style={{ marginRight: 8 }} />ดาวน์โหลดทั้งหมด ({files.length} ไฟล์)</span>
                  {totalSize > 0 && (
                    <span style={{ color: "#888", fontSize: "12px", fontWeight: 400 }}>
                      ({totalSize.toFixed(2)} MB)
                    </span>
                  )}
                </div>
              ),
              onClick: handleDownloadAll,
            },
            { type: "divider" as const }] : []),
            // Individual file options
            ...files.map((file: { id: string; name: string; filepath: string; filesize_mb?: number }) => ({
              key: file.id,
              label: (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
                  <span>{file.name}</span>
                  {file.filesize_mb != null && (
                    <span style={{ color: "#888", fontSize: "12px" }}>
                      ({file.filesize_mb.toFixed(2)} MB)
                    </span>
                  )}
                </div>
              ),
              onClick: () => handleDownload(file.id),
            })),
          ];

          return (
            <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
              <Button type="primary" icon={<DownloadOutlined />}></Button>
            </Dropdown>
          );
        } catch (e) {
          console.error("Error parsing file_path:", e);
          return "-";
        }
      },
    },
    // Show Publish column only for Super Admin and Administrator
    ...(["Super Admin", "Administrator"].includes(auth.user.role)
      ? [
          {
            title: (
              <div style={{ fontSize: "18px", textAlign: "center" }}>
                {t("Publish")}
              </div>
            ),
            key: "publish_toggle",
            width: "10%",
            align: "center" as const,
            render: (_: any, record: DataType) => (
              <Switch
                checked={record.publish_status === 1}
                onChange={() =>
                  handleToggleStatus(record.corpus_id, record.publish_status)
                }
                checkedChildren="เปิด"
                unCheckedChildren="ปิด"
                disabled={auth.user.role !== "Super Admin"}
              />
            ),
          },
        ]
      : []),
    // Show Action column only for Super Admin and Administrator
    ...(["Super Admin"].includes(auth.user.role)
      ? [
          {
            title: (
              <div style={{ fontSize: "18px", textAlign: "center" }}>
                {t("Action")}
              </div>
            ),
            key: "action",
            width: "10%",
            align: "center" as const,
            render: (_: any, record: DataType) => (
              <Row justify="center" align="middle">
                {auth.user.role === "Super Admin" && (
                  <>
                    <Tooltip placement="bottom" title="Edit">
                      <Col
                        style={{
                          cursor: "pointer",
                          marginRight: "8px",
                        }}
                        onClick={() => {
                          navigate("/corpus/edit/" + record.corpus_id);
                        }}
                      >
                        <Button
                          style={{ backgroundColor: "#fff4cc" }}
                          icon={<EditOutlined style={{ color: "#FFB84C" }} />}
                        />
                      </Col>
                    </Tooltip>

                    <Tooltip placement="bottom" title="Delete">
                      <Col
                        style={{
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          showDeleteConfirm(record.corpus_id, record.title);
                        }}
                      >
                        <Button
                          style={{ backgroundColor: "#FFDFDF" }}
                          icon={<DeleteOutlined style={{ color: "#B31312" }} />}
                        />
                      </Col>
                    </Tooltip>
                  </>
                )}
              </Row>
            ),
          },
        ]
      : []),
  ];

  return (
    <Layout>
      <Content className="category-head">
        {/* head */}
        <div>
          <Row justify="space-between" align="middle">
            <Breadcrumb
              separator=">"
              items={[
                {
                  title: (
                    <span style={{ fontWeight: "bold" }}>{t("Corpus")}</span>
                  ),
                },
              ]}
            />
            <Col>
              <Search
                placeholder="Search"
                onChange={(e) => onSearch(e.target.value)}
                onSearch={onSearch}
                style={{ width: 300 }}
              />
              {auth.user.role === "Super Admin" ? (
                <>
                  <Button
                    style={{
                      marginLeft: "10px",
                      backgroundColor: "#00215E",
                      color: "white",
                    }}
                    type="primary"
                    key="primary"
                    onClick={async () => {
                      navigate("/corpus/create");
                    }}
                  >
                    <PlusOutlined />
                    {t("Add Corpus")}
                  </Button>
                </>
              ) : null}
            </Col>
          </Row>
          <hr />
        </div>
      </Content>

      {/* table */}
      <Content className="category-table">
        {isLoading ? (
          <Row justify="center" align="middle" style={{ height: "50vh" }}>
            <Spin size="large" />
          </Row>
        ) : (
          <Table
            rowKey="corpus_id"
            className="custom-table-news"
            columns={columns}
            dataSource={filteredData}
            pagination={{
              showTotal: (total, range) =>
                `แสดงรายการต่อหน้า : ${range[0]}-${range[1]} จาก ${total} รายการ`,
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
            }}
            scroll={{ x: 1000 }}
          />
        )}
      </Content>
    </Layout>
  );
};

export default Corpuspage;
